import {
  anyIdCompanion,
  opaqueId,
  type AnyId,
  type Opaque,
} from "../schemas/branded-id.schemas.js";

/**
 * An opaque, composable error-message string. It is produced ONLY by
 * {@link formatError} and consumed ONLY by {@link BaseError} — both defined in
 * this file, so neither the brand's `unsafeCreate` nor its `unwrap` is ever
 * exported. That makes rendering a branded id into human-readable error text the
 * one sanctioned brand→string path, with no general escape hatch to misuse.
 */
export type ErrorMessage = Opaque<"ErrorMessage">;

/** A single {@link formatError} argument: plain text or any branded id. */
export type ErrorMessagePart = string | AnyId;

/** What an error constructor accepts as its message: plain text or a composed
 * {@link ErrorMessage}. A plain string provably carries no id. */
export type ErrorMessageInput = string | ErrorMessage;

const errorMessageCompanion = opaqueId("ErrorMessage").companion;

/**
 * Compose plain text and branded ids (and other {@link ErrorMessage}s, since an
 * ErrorMessage is itself an id-like brand) into an ErrorMessage. The id→string
 * unwrap happens here, inside this boundary file, so callers keep ids branded
 * end to end and never unwrap to build a message.
 */
export function formatError(
  ...parts: readonly ErrorMessagePart[]
): ErrorMessage {
  return errorMessageCompanion.unsafeCreate(
    parts.map((part) => anyIdCompanion.unwrap(part)).join(""),
  );
}

/**
 * JSON-serializable values carried in {@link BaseError.data}. The leaf admits
 * branded ids ({@link Opaque}`<string>`) recursively, so structured error data
 * can reference ids without unwrapping them. At runtime a branded id is a plain
 * string, so serializing the envelope is unaffected — this is a type-level
 * affordance only.
 */
export type ErrorDataValueType =
  | string
  | number
  | boolean
  | null
  | Opaque<string>
  | ErrorDataObjectType
  | ErrorDataValueType[];

export type ErrorDataObjectType = { [key: string]: ErrorDataValueType };

/** Options every {@link BaseError} accepts. `status` defaults to 500. */
export type BaseErrorOptions = {
  status?: number;
  code?: string;
  data?: ErrorDataObjectType;
  // eslint-disable-next-line planta-v2/unknown-only-at-boundaries -- an error cause is genuinely unknown (may originate outside our code)
  cause?: unknown;
};

/**
 * Root of every error hierarchy that may embed a branded id in its message. It
 * is the ONLY place an {@link ErrorMessage} is unwrapped back to the native
 * `Error` string; subclasses forward a `string | ErrorMessage` up to here and
 * never unwrap themselves. A plain `string` message provably carries no id
 * (interpolating one trips `no-base-to-string`), so `formatError` remains the
 * only way an id reaches a message.
 *
 * It also carries the HTTP-mappable `status` (a semantic error category that
 * happens to align with HTTP), plus optional `code` (the stable machine-readable
 * contract) and `data`. `GlobalExceptionFilter` reads these to build the
 * response envelope. The concrete children below fix `status`; every thrown
 * error extends one of them.
 */
export abstract class BaseError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly data?: ErrorDataObjectType;

  constructor(message: ErrorMessageInput, options?: BaseErrorOptions) {
    super(
      typeof message === "string"
        ? message
        : errorMessageCompanion.unwrap(message),
      options?.cause !== undefined ? { cause: options.cause } : undefined,
    );
    this.status = options?.status ?? 500;
    if (options?.code !== undefined) {
      this.code = options.code;
    }
    if (options?.data !== undefined) {
      this.data = options.data;
    }
    this.name = new.target.name;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Options for a concrete status child: everything except the fixed `status`. */
type StatusErrorOptions = Omit<BaseErrorOptions, "status">;

/** 400 — malformed or invalid client input. */
export class BadRequestError extends BaseError {
  constructor(message: ErrorMessageInput, options?: StatusErrorOptions) {
    super(message, { ...options, status: 400 });
  }
}

/** 401 — request is not authenticated. */
export class UnauthorizedError extends BaseError {
  constructor(message: ErrorMessageInput, options?: StatusErrorOptions) {
    super(message, { ...options, status: 401 });
  }
}

/** 403 — authenticated but not allowed. */
export class ForbiddenError extends BaseError {
  constructor(message: ErrorMessageInput, options?: StatusErrorOptions) {
    super(message, { ...options, status: 403 });
  }
}

/** 404 — a resource the client asked for does not exist. */
export class NotFoundError extends BaseError {
  constructor(message: ErrorMessageInput, options?: StatusErrorOptions) {
    super(message, { ...options, status: 404 });
  }
}

/** 409 — a client-resolvable conflict with current state. */
export class ConflictError extends BaseError {
  constructor(message: ErrorMessageInput, options?: StatusErrorOptions) {
    super(message, { ...options, status: 409 });
  }
}

/** 413 — the client sent a payload larger than the endpoint accepts. */
export class PayloadTooLargeError extends BaseError {
  constructor(message: ErrorMessageInput, options?: StatusErrorOptions) {
    super(message, { ...options, status: 413 });
  }
}

/** 429 — the client exceeded a rate limit. */
export class TooManyRequestsError extends BaseError {
  constructor(message: ErrorMessageInput, options?: StatusErrorOptions) {
    super(message, { ...options, status: 429 });
  }
}

/** 500 — our fault: bug, infra, data corruption, provider failure, invariant. */
export class InternalServerError extends BaseError {
  constructor(message: ErrorMessageInput, options?: StatusErrorOptions) {
    super(message, { ...options, status: 500 });
  }
}
